import { Product } from "../pages/manage-products/manageProducts";

const STORAGE_KEY = "rudrix_product_warranty_info";

export interface ProductWarrantyInfo {
  hasWarranty: boolean;
  warrantyPeriod?: string;
}

export const COMMON_WARRANTY_OPTIONS = [
  "6 Months Warranty",
  "1 Year Warranty",
  "2 Years Warranty",
  "3 Years Warranty",
  "Lifetime Warranty",
];

/**
 * Retrieve all stored warranty configurations from localStorage.
 */
export const getStoredWarrantyMap = (): Record<string, ProductWarrantyInfo> => {
  if (typeof window === "undefined" || !window.localStorage) return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error("Error reading warranty info from localStorage", e);
    return {};
  }
};

/**
 * Retrieve warranty config for a specific product ID.
 */
export const getStoredWarrantyInfo = (productId: string): ProductWarrantyInfo | null => {
  if (!productId) return null;
  const map = getStoredWarrantyMap();
  return map[productId] || null;
};

/**
 * Save warranty config for a specific product ID.
 */
export const setStoredWarrantyInfo = (
  productId: string,
  info: ProductWarrantyInfo
): void => {
  if (!productId || typeof window === "undefined" || !window.localStorage) return;
  try {
    const map = getStoredWarrantyMap();
    map[productId] = {
      hasWarranty: Boolean(info.hasWarranty),
      warrantyPeriod: info.warrantyPeriod || (info.hasWarranty ? "1 Year Warranty" : ""),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (e) {
    console.error("Error saving warranty info to localStorage", e);
  }
};

/**
 * Remove warranty info for a product.
 */
export const removeStoredWarrantyInfo = (productId: string): void => {
  if (!productId || typeof window === "undefined" || !window.localStorage) return;
  try {
    const map = getStoredWarrantyMap();
    delete map[productId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (e) {
    console.error("Error removing warranty info from localStorage", e);
  }
};

/**
 * Normalizes boolean value from various formats (boolean, string "true"/"false", 1/0)
 */
export const parseBooleanValue = (val: any): boolean | undefined => {
  if (val === undefined || val === null) return undefined;
  if (typeof val === "boolean") return val;
  if (typeof val === "string") {
    const lower = val.toLowerCase().trim();
    if (lower === "true" || lower === "1" || lower === "yes") return true;
    if (lower === "false" || lower === "0" || lower === "no") return false;
  }
  if (typeof val === "number") return val === 1;
  return undefined;
};

/**
 * Enrich a product with warranty data (API field takes precedence, then localStorage).
 */
export const enrichProductWithWarranty = (product: any): Product => {
  if (!product) return product;

  const productId = product._id || product.id || "";
  const stored = getStoredWarrantyInfo(productId);

  const directHasWarranty = parseBooleanValue(
    product.hasWarranty !== undefined
      ? product.hasWarranty
      : product.has_warranty !== undefined
      ? product.has_warranty
      : product.isWarranty !== undefined
      ? product.isWarranty
      : undefined
  );

  const hasWarranty =
    directHasWarranty !== undefined
      ? directHasWarranty
      : stored !== null
      ? stored.hasWarranty
      : false;

  const directPeriod = (
    product.warrantyPeriod ||
    product.warranty_period ||
    product.warranty ||
    ""
  ).toString().trim();

  const warrantyPeriod =
    directPeriod ||
    stored?.warrantyPeriod ||
    (hasWarranty ? "1 Year Warranty" : "");

  return {
    ...product,
    hasWarranty,
    has_warranty: hasWarranty,
    warrantyPeriod,
    warranty_period: warrantyPeriod,
  };
};

export interface WarrantyRegistrationRecord {
  _id: string;
  registrationNumber: string;
  customerName: string;
  email: string;
  emailNotifications: boolean;
  mobileNumber: string;
  productName: string;
  productId?: string;
  dateOfPurchase: string;
  purchasedFrom: string;
  orderId: string;
  invoiceUrl: string;
  invoiceOriginalName?: string;
  invoiceMimeType?: string;
  invoiceSize?: number;
  storeId?: string;
  storeUrl?: string;
  status: "pending" | "verified" | "rejected";
  createdAt: string;
  updatedAt?: string;
  isLocallySaved?: boolean;
}

const REGISTRATIONS_STORAGE_KEY = "rudrix_warranty_registrations";

/**
 * Get all stored warranty registrations from local storage.
 */
export const getStoredWarrantyRegistrations = (): WarrantyRegistrationRecord[] => {
  if (typeof window === "undefined" || !window.localStorage) return [];
  try {
    const raw = localStorage.getItem(REGISTRATIONS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error("Error reading warranty registrations from localStorage", e);
    return [];
  }
};

/**
 * Save a new warranty registration record to local storage.
 */
export const saveStoredWarrantyRegistration = (
  record: WarrantyRegistrationRecord
): void => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const current = getStoredWarrantyRegistrations();
    // Prepend new record, deduplicate by registrationNumber or _id
    const filtered = current.filter(
      (r) =>
        r._id !== record._id &&
        r.registrationNumber !== record.registrationNumber &&
        !(r.orderId === record.orderId && r.customerName === record.customerName)
    );
    filtered.unshift(record);
    localStorage.setItem(REGISTRATIONS_STORAGE_KEY, JSON.stringify(filtered));
  } catch (e) {
    console.error("Error saving warranty registration to localStorage", e);
  }
};

/**
 * Update the status of a registration in local storage.
 */
export const updateStoredWarrantyRegistrationStatus = (
  idOrRegNum: string,
  status: "pending" | "verified" | "rejected"
): void => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const current = getStoredWarrantyRegistrations();
    const updated = current.map((r) => {
      if (r._id === idOrRegNum || r.registrationNumber === idOrRegNum) {
        return { ...r, status, updatedAt: new Date().toISOString() };
      }
      return r;
    });
    localStorage.setItem(REGISTRATIONS_STORAGE_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error("Error updating warranty registration status in localStorage", e);
  }
};

/**
 * Delete a registration from local storage.
 */
export const deleteStoredWarrantyRegistration = (idOrRegNum: string): void => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const current = getStoredWarrantyRegistrations();
    const filtered = current.filter(
      (r) => r._id !== idOrRegNum && r.registrationNumber !== idOrRegNum
    );
    localStorage.setItem(REGISTRATIONS_STORAGE_KEY, JSON.stringify(filtered));
  } catch (e) {
    console.error("Error deleting warranty registration from localStorage", e);
  }
};

/**
 * Returns the URL path to the dedicated warranty registration page.
 */
export const getWarrantyPageUrl = (storeUrl?: string): string => {
  if (typeof window === "undefined") return "/warranty-registration";
  const hostname = window.location.hostname.toLowerCase().replace(/^www\./, "");
  const isCustomDomain =
    hostname !== "app.therudrix.in" &&
    !hostname.startsWith("admin.") &&
    hostname !== "localhost" &&
    hostname !== "127.0.0.1";

  if (isCustomDomain) {
    return "/warranty-registration";
  }

  const cleanUrl = storeUrl
    ? storeUrl.toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "")
    : "shop";
  return `/${cleanUrl}/warranty-registration`;
};

